import React, { useState, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';

const ProtectedRoute = ({ children }) => {
    const [isChecking, setIsChecking] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const { tokens } = await fetchAuthSession();
                setIsAuthenticated(!!tokens?.idToken);
            } catch (error) {
                console.error('Auth check failed:', error);
                setIsAuthenticated(false);
            } finally {
                setIsChecking(false);
            }
        };
        checkAuth();
    }, []);

    if (isChecking) {
        return (
            <div className="min-h-screen w-full flex flex-col items-center justify-center bg-[#020617] relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full">
                    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-[120px] animate-pulse" />
                    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
                </div>
                <div className="relative z-10 p-12 rounded-[2.5rem] border border-white/10 bg-white/5 backdrop-blur-3xl shadow-2xl flex flex-col items-center gap-8">
                    <div className="relative w-20 h-20">
                        <div className="absolute inset-0 rounded-full border-[3px] border-cyan-500/20" />
                        <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-cyan-400 animate-spin" style={{ animationDuration: '1.5s' }} />
                        <div className="absolute inset-2 rounded-full border-[2px] border-blue-500/20" />
                        <div className="absolute inset-2 rounded-full border-[2px] border-transparent border-t-blue-400 animate-spin" style={{ animationDuration: '2s', animationDirection: 'reverse' }} />
                        <div className="absolute inset-6 bg-gradient-to-tr from-cyan-400 to-blue-500 rounded-full blur-[10px] animate-pulse" />
                        <div className="absolute inset-6 bg-gradient-to-tr from-cyan-400 to-blue-500 rounded-full" />
                    </div>
                    <div className="flex flex-col items-center gap-2">
                        <h3 className="text-xl font-bold text-white tracking-tight">Authenticating</h3>
                        <p className="text-cyan-400/60 text-sm font-medium tracking-widest uppercase">Secure Handshake in progress</p>
                    </div>
                </div>
                <div className="absolute bottom-12 left-0 w-full flex justify-center opacity-20 pointer-events-none">
                    <div className="text-sm font-bold tracking-[0.4em] uppercase text-white">BytesizedAI Architect</div>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // After social login (Google/GitHub), Cognito redirects to '/'.
    // Check if we saved a redirect URL before the OAuth flow.
    const savedRedirect = localStorage.getItem('bytesized_login_redirect');
    if (savedRedirect && location.pathname + location.search !== savedRedirect) {
        localStorage.removeItem('bytesized_login_redirect');
        navigate(savedRedirect, { replace: true });
        return null;
    }
    if (savedRedirect) {
        localStorage.removeItem('bytesized_login_redirect');
    }

    return children;
};

export default ProtectedRoute;
